import React from 'react'
import { Logger } from 'wdc-cube'
import { makeStyles } from 'tss-react/mui'
import { styled } from '@mui/material/styles'
import {
    classToFComponent,
    FCClassContext,
    ModuleView,
    PageSubtitle,
    PageTitle,
    ScrollablePageView
} from 'src/utils/views'

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import FilterPaneView from './filters/tcm_filter_pane_view'

import ChartTotalEmpresasView from './tcm_chart_total_empresas'
import ChartTotalEmpresasAtivasView from './tcm_chart_total_empresas_ativas'
import ChartTotalEmpresasNovasUltMesView from './tcm_chart_total_novas_ult_mes'
import ChartFilialMatrizView from './tcm_chart_filial_matriz'
import ChartEmpresasAtivasPorUFView from './tcm_chart_total_empresas_ativas_por_uf'
import ChartNaturezaJuridicaView from './tcm_chart_natureza_juridica'
import AtividadeEconomicaTableView from './tcm_ativ_economica'
import ChartAbertasFechadasView from './tcm_chart_abertas_fechadas'
import ChartFaixasView from './helpers/tcm_chart_faixas'
import MapCardView from './tcm_map_card'
import ListagemTabsView from './tcm_listagem_tabs'

import { AppliedFilterSummary, TheConsumerMarketScope } from '../tcm_scopes'

const LOG = Logger.get('TCM-VIEW')

const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 12,
    '@media (max-width: 720px)': {
        flexDirection: 'column',
        gap: 12
    }
})

const InfoButton = styled(IconButton)({
    width: 34,
    height: 34,
    flex: '0 0 auto',
    borderRadius: 8,
    color: 'var(--muted)',
    '&:hover': { color: 'var(--green-800)', backgroundColor: 'var(--green-050)' },
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

const HeadActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: '0 0 auto'
})

const FilterButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
    ({ active }) => ({
        width: 34,
        height: 34,
        flex: '0 0 auto',
        borderRadius: 8,
        color: active ? 'var(--green-800)' : 'var(--muted)',
        backgroundColor: active ? 'var(--green-050)' : 'transparent',
        '&:hover': { color: 'var(--green-800)', backgroundColor: 'var(--green-050)' },
        '& .MuiSvgIcon-root': { fontSize: 20 }
    })
)

const filterBadgeSx = {
    '& .MuiBadge-badge': {
        top: 2,
        right: 2,
        height: 14,
        minWidth: 14,
        padding: '0 4px',
        fontSize: 9.5,
        fontWeight: 700,
        backgroundColor: 'var(--green-800)',
        color: '#fff'
    }
}

const TipRow = styled('div')({
    display: 'flex',
    gap: 10,
    justifyContent: 'space-between',
    lineHeight: 1.5,
    whiteSpace: 'nowrap'
})

const TipLabel = styled('span')({
    color: 'rgba(255, 255, 255, .68)'
})

const TipValue = styled('span')({
    fontWeight: 700
})

const TipHead = styled('div')({
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.4px',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, .5)',
    marginBottom: 8
})

const TipList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    maxHeight: 320,
    overflowY: 'auto'
})

const TipFilterRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(80px, max-content) 1fr',
    gap: 10,
    alignItems: 'baseline'
})

const TagRow = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4
})

const TagPrefix = styled('span')({
    fontSize: 11,
    color: 'rgba(255, 255, 255, .5)'
})

const Tag = styled('span')({
    borderRadius: 999,
    border: '1px solid rgba(255, 255, 255, .16)',
    backgroundColor: 'rgba(255, 255, 255, .08)',
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap'
})

const TagMore = styled(Tag)({
    color: '#9fe0a6',
    borderColor: 'rgba(159, 224, 166, .32)',
    backgroundColor: 'rgba(159, 224, 166, .12)'
})

const infoTooltipSx = {
    backgroundColor: '#171b18',
    border: '1px solid #2d382f',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: 12,
    boxShadow: 'var(--shadow-2)'
}

const filterTooltipSx = {
    ...infoTooltipSx,
    maxWidth: 460
}

const TILE_ROW = 88
const SUPPORT_ROW = 320
const CARD_GAP = 8
const TOP_ROW_GAP = 10
const CORNER_HEIGHT = TILE_ROW + CARD_GAP + SUPPORT_ROW + CARD_GAP + SUPPORT_ROW
const TOP_ROW = (CORNER_HEIGHT - TOP_ROW_GAP) / 2

const useStyles = makeStyles()({
    overlay: {
        flexShrink: 0,
        marginRight: 5,
        marginBottom: 5
    },
    topGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(400px, .5fr)',
        gridTemplateRows: `${TOP_ROW}px ${TOP_ROW}px`,
        gridTemplateAreas: '"corner evolucao" "corner atividade"',
        columnGap: 11,
        rowGap: TOP_ROW_GAP,
        '@media (max-width: 1240px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: `${CORNER_HEIGHT}px 340px 340px`,
            gridTemplateAreas: '"corner" "evolucao" "atividade"'
        }
    },
    cornerGrid: {
        gridArea: 'corner',
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px minmax(0, 1fr)',
        gridTemplateRows: `${TILE_ROW}px ${SUPPORT_ROW}px ${SUPPORT_ROW}px`,
        gridTemplateAreas: '"total ativas novas filiais" "uf uf uf mapa" "natureza natureza natureza mapa"',
        gap: CARD_GAP,
        '@media (max-width: 1050px)': {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gridTemplateRows: `${TILE_ROW}px ${TILE_ROW}px ${SUPPORT_ROW}px ${SUPPORT_ROW}px ${SUPPORT_ROW}px`,
            gridTemplateAreas: `
                "total ativas novas"
                "filiais filiais filiais"
                "mapa mapa mapa"
                "uf uf uf"
                "natureza natureza natureza"
            `
        },
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: `repeat(4, ${TILE_ROW}px) repeat(3, 300px)`,
            gridTemplateAreas: `
                "total"
                "ativas"
                "novas"
                "filiais"
                "mapa"
                "uf"
                "natureza"
            `
        }
    },
    totalEmpresas: { gridArea: 'total' },
    totalEmpresasAtivas: { gridArea: 'ativas' },
    totalUltimoMes: { gridArea: 'novas' },
    filialMatriz: { gridArea: 'filiais' },
    totalEmpresasAtivasPorUF: { gridArea: 'uf' },
    naturezaJuridica: { gridArea: 'natureza' },
    mapa: { gridArea: 'mapa' },
    novasEmpresas: { gridArea: 'evolucao' },
    detalhamentoAtivPrincipal: { gridArea: 'atividade' },
    faixasRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 8,
        '@media (max-width: 1240px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
        },
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    faixaCard: {
        height: 340
    },
    detalhamentoDasEmpresas: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minWidth: 0
    },
    mapaMaximizado: {
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        padding: '26px 28px 42px',
        '@media (max-width: 900px)': {
            padding: '22px 18px 36px'
        }
    },
    tabelaMaximizada: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        padding: '26px 28px 42px',
        '@media (max-width: 900px)': {
            padding: '22px 18px 36px'
        }
    }
})

type TheConsumerMarketProps = {
    className?: string
    scope: TheConsumerMarketScope
}

class TheConsumerMarketViewClass implements FCClassContext<TheConsumerMarketProps> {
    // :: State

    scope!: TheConsumerMarketScope
    classes!: ReturnType<typeof useStyles>['classes']

    // :: Emissors

    readonly emitOnWndKeyDown = (evt: KeyboardEvent) => {
        this.emitCtrlKeyToggled(evt)
    }

    readonly emitOnWndKeyUp = (evt: KeyboardEvent) => {
        this.emitCtrlKeyToggled(evt)
    }

    readonly emitOnMouseMouve = (evt: MouseEvent) => {
        this.emitCtrlKeyToggled(evt)
    }

    private emitCtrlKeyToggled(evt: { ctrlKey: boolean; metaKey: boolean }) {
        const previousCtrlKey = this.scope.keyboard.ctrlKey
        const newCtrlKey = evt.ctrlKey || evt.metaKey
        this.scope.keyboard.ctrlKey = newCtrlKey
        if (previousCtrlKey !== newCtrlKey) {
            this.scope.onCtrlKeyToggled(newCtrlKey).catch(LOG.caught)
        }
    }

    // :: Lifecicle listeners

    onSyncState({ scope }: TheConsumerMarketProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    onAttach() {
        document.addEventListener('keydown', this.emitOnWndKeyDown)
        document.addEventListener('keyup', this.emitOnWndKeyUp)
        document.addEventListener('mousemove', this.emitOnMouseMouve)
    }

    onDetach() {
        document.removeEventListener('keydown', this.emitOnWndKeyDown)
        document.removeEventListener('keyup', this.emitOnWndKeyUp)
        document.addEventListener('mousemove', this.emitOnMouseMouve)
    }

    render(props: TheConsumerMarketProps) {
        const { scope } = this
        if (scope.maximizeCard === 'mapa') {
            return this.renderMapOnly(props)
        } else if (scope.maximizeCard === 'table') {
            return this.renderTableOnly(props)
        }

        return this.renderAll(props)
    }

    renderPageHead() {
        const { dataAtualizacao, dataDeCorte, appliedFilters } = this.scope

        return (
            <PageHead>
                <div>
                    <PageTitle>Mercado IA</PageTitle>
                    <PageSubtitle>
                        Explore o universo de empresas por porte, atividade, localização e vínculos — clique em qualquer
                        indicador para restringir o recorte.
                    </PageSubtitle>
                </div>
                <HeadActions>
                    {this.renderAppliedFiltersButton(appliedFilters)}
                    {(!!dataAtualizacao || !!dataDeCorte) && (
                        <Tooltip
                            arrow
                            placement="left"
                            title={
                                <>
                                    {!!dataAtualizacao && (
                                        <TipRow>
                                            <TipLabel>Atualizado em</TipLabel>
                                            <TipValue>{dataAtualizacao}</TipValue>
                                        </TipRow>
                                    )}
                                    {!!dataDeCorte && (
                                        <TipRow>
                                            <TipLabel>Data de corte</TipLabel>
                                            <TipValue>{dataDeCorte}</TipValue>
                                        </TipRow>
                                    )}
                                </>
                            }
                            slotProps={{ tooltip: { sx: infoTooltipSx } }}
                        >
                            <InfoButton size="small" aria-label="Datas de referência dos dados">
                                <InfoOutlinedIcon />
                            </InfoButton>
                        </Tooltip>
                    )}
                </HeadActions>
            </PageHead>
        )
    }

    renderAppliedFiltersButton(filters: AppliedFilterSummary[]) {
        const count = filters.length

        return (
            <Tooltip
                arrow
                placement="left"
                title={this.renderAppliedFiltersTip(filters)}
                slotProps={{ tooltip: { sx: count > 0 ? filterTooltipSx : infoTooltipSx } }}
            >
                <FilterButton
                    size="small"
                    active={count > 0}
                    aria-label={count > 0 ? `${count} campos filtrados` : 'Sem filtro aplicado'}
                >
                    <Badge sx={filterBadgeSx} badgeContent={count} overlap="circular">
                        <FilterAltOutlinedIcon />
                    </Badge>
                </FilterButton>
            </Tooltip>
        )
    }

    renderAppliedFiltersTip(filters: AppliedFilterSummary[]) {
        if (filters.length === 0) {
            return (
                <TipRow>
                    <TipLabel>Sem filtro aplicado</TipLabel>
                </TipRow>
            )
        }

        return (
            <>
                <TipHead>{filters.length === 1 ? '1 campo filtrado' : `${filters.length} campos filtrados`}</TipHead>
                <TipList>
                    {filters.map((filter, index) => (
                        <TipFilterRow key={`${filter.caption}-${index}`}>
                            <TipLabel>{filter.caption}</TipLabel>
                            <TagRow>
                                {filter.parts.map((part, partIndex) => (
                                    <React.Fragment key={partIndex}>
                                        {!!part.prefix && <TagPrefix>{part.prefix}</TagPrefix>}
                                        {part.tags.map((tag, tagIndex) => (
                                            <Tag key={`${tag}-${tagIndex}`}>{tag}</Tag>
                                        ))}
                                        {part.hiddenCount > 0 && <TagMore>+{part.hiddenCount}</TagMore>}
                                    </React.Fragment>
                                ))}
                            </TagRow>
                        </TipFilterRow>
                    ))}
                </TipList>
            </>
        )
    }

    renderMapOnly(props: TheConsumerMarketProps) {
        const { classes, scope } = this
        return (
            <ModuleView className={props.className}>
                <div className={classes.mapaMaximizado}>
                    <MapCardView scope={scope.mapa} />
                </div>
                <FilterPaneView className={classes.overlay} scope={scope.filterPane} />
            </ModuleView>
        )
    }

    renderTableOnly(props: TheConsumerMarketProps) {
        const { classes, scope } = this
        return (
            <ModuleView className={props.className}>
                <div className={classes.tabelaMaximizada}>
                    <ListagemTabsView className={classes.detalhamentoDasEmpresas} scope={scope.listagemTabs} />
                </div>
            </ModuleView>
        )
    }

    renderAll(props: TheConsumerMarketProps) {
        const { classes, scope } = this
        return (
            <ModuleView className={props.className}>
                <ScrollablePageView>
                    {this.renderPageHead()}

                    <div className={classes.topGrid}>
                        <div className={classes.cornerGrid}>
                            <ChartTotalEmpresasView className={classes.totalEmpresas} scope={scope.totalEmpresas} />
                            <ChartTotalEmpresasAtivasView
                                className={classes.totalEmpresasAtivas}
                                scope={scope.totalEmpresasAtivas}
                            />
                            <ChartTotalEmpresasNovasUltMesView
                                className={classes.totalUltimoMes}
                                scope={scope.totalEmpresasNovasUltimoMes}
                            />
                            <ChartFilialMatrizView className={classes.filialMatriz} scope={scope.filialMatriz} />
                            <ChartEmpresasAtivasPorUFView
                                className={classes.totalEmpresasAtivasPorUF}
                                scope={scope.empresasAtivasPorUF}
                            />
                            <ChartNaturezaJuridicaView
                                className={classes.naturezaJuridica}
                                scope={scope.naturezaJuridica}
                            />
                            <MapCardView className={classes.mapa} scope={scope.mapa} />
                        </div>

                        <ChartAbertasFechadasView className={classes.novasEmpresas} scope={scope.abertasFechadas} />
                        <AtividadeEconomicaTableView
                            className={classes.detalhamentoAtivPrincipal}
                            scope={scope.atividadeEconomica}
                        />
                    </div>

                    <div className={classes.faixasRow}>
                        <ChartFaixasView scope={scope.empresasNoGrupo} className={classes.faixaCard} />
                        <ChartFaixasView scope={scope.faixaDeIdade} className={classes.faixaCard} />
                        <ChartFaixasView scope={scope.faixaDeFuncionarios} className={classes.faixaCard} />
                    </div>

                    <div className={classes.faixasRow}>
                        <ChartFaixasView scope={scope.faixaDeFuncionariosNoGrupo} className={classes.faixaCard} />
                        <ChartFaixasView scope={scope.faixaDeFaturamentoAnual} className={classes.faixaCard} />
                        <ChartFaixasView
                            scope={scope.faixaDeFaturamentoConsolidadoGrupo}
                            className={classes.faixaCard}
                        />
                    </div>

                    <ListagemTabsView className={classes.detalhamentoDasEmpresas} scope={scope.listagemTabs} />
                </ScrollablePageView>
                <FilterPaneView className={classes.overlay} scope={scope.filterPane} />
            </ModuleView>
        )
    }
}

export default classToFComponent(TheConsumerMarketViewClass, React)
